import { useSidebar } from "@universe/ui/components/sidebar";
import { useIsMobile } from "@universe/ui/hooks/use-mobile";
import { cn } from "@universe/ui/lib/utils";
import { motion } from "framer-motion";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useRef, useState } from "react";

import { AppLink } from "@/components/app-link";
import CurrencyFlag from "@/components/currency-flag";
import StickyHeader from "@/components/sticky-header";
import { PRODUCT_TYPE } from "@/constants";
import useBaseCurrencyConverter from "@/hooks/useBaseCurrencyConverter";
import useCurrencyFormatter from "@/hooks/useCurrencyFormatter";
import { useReturnTo } from "@/hooks/useReturnTo";
import { useWindowSize } from "@/hooks/useWindowSize";
import type { AccountSummaryItem } from "@/utils/buildAccountSummary";

import { AccountsSummarySkeleton } from "./dashboard-skeleton";
import ViewAllAccountsCard from "./view-all-accounts-card";

export type { AccountSummaryItem };

/** Shared card sizing classes — used by AccountBalanceCard, ViewAllAccountsCard, and AccountsSummarySkeleton */
/** Fixed width so balance and view-all cards align in the carousel. */
export const ACCOUNT_CARD_CLASSES =
  "w-[200px] min-w-[200px] h-[130px] lg:w-[220px] lg:min-w-[220px] lg:h-[140px]" as const;

export const ACCOUNT_CARD_PADDING = "p-3" as const;

type AccountBalanceCardProps = {
  account: AccountSummaryItem;
  baseCurrency: string;
};

const AccountBalanceCard = ({
  account,
  baseCurrency,
}: AccountBalanceCardProps) => {
  const { formatCurrency } = useCurrencyFormatter();
  const { convert, isBaseCurrency } = useBaseCurrencyConverter();
  const isMobile = useIsMobile();
  const tAccounts = useTranslations("accounts");

  const currency =
    account.type === PRODUCT_TYPE.CURRENCIES
      ? account.currency
      : account.product;
  const { balance } = account;

  const isBase = isBaseCurrency(currency);
  const baseCurrencyBalance = useMemo(
    () => convert(balance, currency),
    [balance, currency, convert]
  );

  return (
    <AppLink
      href={`/accounts/${currency}`}
      className={`group relative bg-brand-primary/6 border border-brand-primary/40 rounded-lg flex flex-col items-center justify-between ${ACCOUNT_CARD_PADDING} hover:border-brand-boundary transition-all ${ACCOUNT_CARD_CLASSES} flex-shrink-0 shadow-sm backdrop-blur-sm hover:shadow-md snap-center overflow-hidden`}
      style={{
        backdropFilter: "blur(4px)",
        transition: "border-color 0.3s ease, box-shadow 0.3s ease",
        boxShadow: "0 1px 4px 0 rgba(0, 0, 0, 0.2)",
      }}
    >
      {/* Hover gradient overlay */}
      <div
        className="absolute inset-0 rounded-lg pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-[1]"
        style={{
          background:
            "radial-gradient(circle at center, oklch(from var(--brand-primary) l c h / 0.00) 0%, oklch(from var(--brand-primary) l c h / 0.1) 100%)",
        }}
      />
      <div className="flex items-center justify-center pt-0.5 mb-2 relative z-[2]">
        <CurrencyFlag currency={currency} size={isMobile ? "36px" : "44px"} />
      </div>
      <div className="flex flex-col items-center w-full pb-1 relative z-[2]">
        <span
          className="text-base lg:text-lg font-semibold text-center break-words whitespace-nowrap"
          data-testid={`balance-amount-${currency}`}
        >
          {formatCurrency(balance, currency)}
        </span>
        {isBase ? (
          <span className="text-xs lg:text-sm text-muted-foreground mt-1 text-center font-medium">
            {tAccounts("baseCurrency")}
          </span>
        ) : (
          <span
            className="text-xs lg:text-sm text-muted-foreground mt-1 text-center whitespace-nowrap"
            data-testid={`balance-gbp-${currency}`}
          >
            {formatCurrency(baseCurrencyBalance, baseCurrency)}
          </span>
        )}
      </div>
    </AppLink>
  );
};

type AccountsSummaryGridProps = {
  accounts?: AccountSummaryItem[];
  isLoading: boolean;
  baseCurrency: string;
  showViewAllAccountsCard?: boolean;
};

const AccountsSummaryGrid = ({
  accounts,
  isLoading,
  baseCurrency,
  showViewAllAccountsCard = false,
}: AccountsSummaryGridProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const { width: windowWidth } = useWindowSize();
  const sidebar = useSidebar();
  const isMobile = useIsMobile();

  // Calculate container width based on sidebar state
  const containerWidth = useMemo(() => {
    if (!windowWidth || isMobile) return undefined;

    // Sidebar widths in rem converted to px (assuming 1rem = 16px)
    const SIDEBAR_WIDTH_EXPANDED = 16 * 16; // 16rem = 256px
    const SIDEBAR_WIDTH_ICON = 3 * 16; // 3rem = 48px

    // On lg and xl screens, subtract sidebar width
    const sidebarWidth = sidebar.open
      ? SIDEBAR_WIDTH_EXPANDED
      : SIDEBAR_WIDTH_ICON;

    // Account for padding and margins (approximate)
    const adjustment = sidebar.open ? 60 : 0; // padding, margins, etc.

    const calculatedWidth = windowWidth - sidebarWidth - adjustment;

    // Constrain to max-width of 1440px (max-w-8xl)
    return Math.min(calculatedWidth, 1440);
  }, [windowWidth, sidebar.open, isMobile]);

  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 1
      );
    }
  };

  useEffect(() => {
    checkScroll();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScroll);
      return () => {
        container.removeEventListener("scroll", checkScroll);
        window.removeEventListener("resize", checkScroll);
      };
    }
  }, [accounts, containerWidth]);

  const scroll = (direction: "left" | "right") => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = 300;
      container.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  if (isLoading) {
    return <AccountsSummarySkeleton />;
  }

  if (!accounts || accounts.length === 0) {
    const fallbackAccount: AccountSummaryItem = {
      type: PRODUCT_TYPE.CURRENCIES,
      currency: baseCurrency,
      balance: 0,
    };
    return (
      <div className="flex gap-4 pt-2 pb-4">
        <AccountBalanceCard
          account={fallbackAccount}
          baseCurrency={baseCurrency}
        />
        {showViewAllAccountsCard && <ViewAllAccountsCard />}
      </div>
    );
  }

  return (
    <div className="mx-auto">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="relative"
      >
        {/* Left fade gradient - Multi-layer for smooth transition */}
        {canScrollLeft && (
          <>
            {/* Base white layer */}
            <div
              className={`hidden md:block absolute left-0 top-0 bottom-0 ${isMobile ? "w-6" : "w-12"} z-[1] pointer-events-none`}
              style={{
                background:
                  "linear-gradient(to right, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0.9) 25%, rgba(255, 255, 255, 0.6) 55%, rgba(255, 255, 255, 0.2) 80%, rgba(255, 255, 255, 0) 100%)",
              }}
            />
            {/* Blur layer */}
            <div
              className={`hidden md:block absolute left-0 top-0 bottom-0 ${isMobile ? "w-6" : "w-12"} z-[1] pointer-events-none`}
              style={{
                backdropFilter: isMobile ? "blur(2px)" : "blur(4px)",
                WebkitMaskImage:
                  "linear-gradient(to right, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.3) 30%, rgba(0, 0, 0, 0.7) 60%, rgba(0, 0, 0, 1) 90%, rgba(0, 0, 0, 0) 100%)",
                maskImage:
                  "linear-gradient(to right, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.3) 30%, rgba(0, 0, 0, 0.7) 60%, rgba(0, 0, 0, 1) 90%, rgba(0, 0, 0, 0) 100%)",
              }}
            />
          </>
        )}

        {/* Right fade gradient - Multi-layer for smooth transition */}
        {canScrollRight && (
          <>
            {/* Base white layer */}
            <div
              className={`hidden md:block absolute right-0 top-0 bottom-0 ${isMobile ? "w-7" : "w-14"} z-[1] pointer-events-none`}
              style={{
                background:
                  "linear-gradient(to left, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0.95) 20%, rgba(255, 255, 255, 0.75) 45%, rgba(255, 255, 255, 0.4) 70%, rgba(255, 255, 255, 0.1) 90%, rgba(255, 255, 255, 0) 100%)",
              }}
            />
            {/* Blur layer */}
            <div
              className={`hidden md:block absolute right-0 top-0 bottom-0 ${isMobile ? "w-7" : "w-14"} z-[1] pointer-events-none`}
              style={{
                backdropFilter: isMobile ? "blur(2px)" : "blur(4px)",
                WebkitMaskImage:
                  "linear-gradient(to left, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.4) 25%, rgba(0, 0, 0, 0.8) 55%, rgba(0, 0, 0, 1) 85%, rgba(0, 0, 0, 0) 100%)",
                maskImage:
                  "linear-gradient(to left, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.4) 25%, rgba(0, 0, 0, 0.8) 55%, rgba(0, 0, 0, 1) 85%, rgba(0, 0, 0, 0) 100%)",
              }}
            />
          </>
        )}

        {canScrollLeft && (
          <button
            onClick={() => scroll("left")}
            className={`absolute left-2 top-1/2 -translate-y-1/2 z-1 bg-white/90 hover:bg-white border-2 border-input rounded-full shadow-sm transition-all ${isMobile ? "p-1.5" : "p-2"}`}
            aria-label="Scroll left"
          >
            <ChevronLeft
              className={`text-gray-700 ${isMobile ? "size-4" : "size-5"}`}
            />
          </button>
        )}
        {canScrollRight && (
          <button
            onClick={() => scroll("right")}
            className={`absolute right-2 top-1/2 -translate-y-1/2 z-1 bg-white/90 hover:bg-white border-2 border-input rounded-full shadow-sm transition-all ${isMobile ? "p-1.5" : "p-2"}`}
            aria-label="Scroll right"
          >
            <ChevronRight
              className={`text-gray-700 ${isMobile ? "size-4" : "size-5"}`}
            />
          </button>
        )}
        <div
          ref={scrollContainerRef}
          className="flex gap-4 overflow-x-auto scrollbar-hide pt-2 pb-4 snap-x snap-mandatory px-0.5"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            maxWidth: containerWidth ? `${containerWidth}px` : undefined,
          }}
        >
          {accounts.map((account) => (
            <AccountBalanceCard
              key={
                account.type === PRODUCT_TYPE.CURRENCIES
                  ? account.currency
                  : account.product
              }
              account={account}
              baseCurrency={baseCurrency}
            />
          ))}
          {showViewAllAccountsCard && <ViewAllAccountsCard />}
        </div>
      </motion.div>
    </div>
  );
};

type AccountsSummaryProps = {
  accounts: AccountSummaryItem[];
  isLoading: boolean;
  baseCurrency: string;
  className?: string;
  showViewAllAccountsCard?: boolean;
};

const AccountsSummary = ({
  accounts,
  isLoading,
  baseCurrency,
  className,
  showViewAllAccountsCard = false,
}: AccountsSummaryProps) => {
  const tAccounts = useTranslations("accounts");
  const { addReturnToParam } = useReturnTo();

  return (
    <>
      <StickyHeader subHeader={2} noPageHeader className="pb-1">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-lg md:text-xl font-semibold">
            {tAccounts("accountSummaryTitle")}
          </h2>
          <AppLink
            href={addReturnToParam("/accounts")}
            className="standard-link flex items-end gap-1.5 text-sm md:text-base"
          >
            <p className="lg:mt-1">{tAccounts("viewAllAccountsLink")}</p>
            <ArrowUpRight className="size-4 text-primary mb-0.5 lg:mb-1 mr-0" />
          </AppLink>
        </div>
      </StickyHeader>
      <div className={cn(className)}>
        <AccountsSummaryGrid
          accounts={accounts}
          isLoading={isLoading}
          baseCurrency={baseCurrency}
          showViewAllAccountsCard={showViewAllAccountsCard}
        />
      </div>
    </>
  );
};

export default AccountsSummary;
